// ============================================================================
// Corporate Facts — the Add / Edit form frame shared by the list editors
// ============================================================================
// Related parties, members, share movements… each list opens its item in
// this dialog: fields in a two-column grid, Cancel / Save. Save hands the
// item back to the list (the Corporate Facts draft); the page's Save stores it.

import React from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';

export const FactsFormShell: React.FC<{
  title: string;
  onCancel: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
  saveHint?: string;
  width?: number;
  children: React.ReactNode;
}> = ({ title, onCancel, onSave, saveDisabled, saveHint, width = 760, children }) => createPortal(
  <div style={overlayStyle} onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
    <div role="dialog" aria-modal="true" aria-label={title} style={{ ...dialogStyle, width: `min(${width}px, 94vw)` }}>
      <div style={headerStyle}>
        <strong style={{ flex: 1 }}>{title}</strong>
        <button type="button" className="modal-close" onClick={onCancel} aria-label="Close"><IconSystem.x size={18} /></button>
      </div>
      <div style={bodyStyle}>{children}</div>
      <div style={footerStyle}>
        {saveHint && <span style={{ flex: 1, fontSize: '0.75rem', color: '#b45309' }}>{saveHint}</span>}
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saveDisabled}>Save</button>
      </div>
    </div>
  </div>,
  document.body,
);

export const FactsField: React.FC<{ label: string; htmlFor?: string; hint?: string; span?: boolean; children: React.ReactNode }> = ({
  label, htmlFor, hint, span, children,
}) => (
  <div className="form-group" style={{ marginBottom: 0, gridColumn: span ? '1 / -1' : undefined }}>
    <label className="form-label" htmlFor={htmlFor}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{hint}</div>}
  </div>
);

export const factsFormGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px' };

/** "1,23,456" / "(1,000)" / "1000.5" → number; blank or invalid → null. */
export function parseFactsNumber(v: string): number | null {
  const t = v.trim().replace(/,/g, '');
  if (!t) return null;
  const neg = /^\(.*\)$/.test(t);
  const n = Number(t.replace(/[()]/g, ''));
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}

// Above the Corporate Facts side sheet (10080), below the action menu (10100).
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 10090, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const dialogStyle: React.CSSProperties = {
  maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)',
  borderRadius: 10, boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
};
const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)',
};
const bodyStyle: React.CSSProperties = { padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 };
const footerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border-color)',
};
